import type { ControlInput } from '../sim/types';
import { approach, clamp } from '../sim/math';

/** Raw intents from a device before filtering. Steer -1 = left, +1 = right. */
export interface RawControls {
  steer: number;
  throttle: number;
  brake: number;
  handbrake: number;
  shiftUp: boolean;
  shiftDown: boolean;
  /** True when steer/throttle/brake come from digital keys (needs rate limiting). */
  digital: boolean;
}

export const emptyRaw = (): RawControls => ({ steer: 0, throttle: 0, brake: 0, handbrake: 0, shiftUp: false, shiftDown: false, digital: true });

export interface VehicleFeel {
  /** Forward speed (m/s). */
  speed: number;
  /** Body slip angle (rad, + = velocity to the left of heading). */
  slipAngle: number;
}

/**
 * Turns device intents into smooth driver inputs. Digital steering is rate-limited, speed
 * sensitive and self-centring; when the car is sliding the lock limit opens up so a keyboard
 * driver can catch and hold a slide. Used by the player and by the keyboard test bot alike.
 */
export class InputSmoother {
  readonly out: ControlInput = { steer: 0, throttle: 0, brake: 0, handbrake: 0, shiftUp: false, shiftDown: false };

  update(raw: RawControls, feel: VehicleFeel, dt: number): ControlInput {
    const o = this.out;
    const v = Math.abs(feel.speed);
    if (raw.digital) {
      const sliding = Math.abs(feel.slipAngle) > 0.12;
      const speedLimit = clamp(1 - (v - 8) / 45, 0.38, 1);
      const lim = sliding ? Math.max(speedLimit, 0.85) : speedLimit;
      const target = clamp(raw.steer, -1, 1) * lim;
      const steerRate = clamp(2.6 - v * 0.035, 1.25, 2.6);
      const returning = Math.abs(target) < Math.abs(o.steer) || Math.sign(target) !== Math.sign(o.steer);
      const rate = returning ? 4.2 : steerRate;
      o.steer = approach(o.steer, target, rate * dt);
      o.throttle = approach(o.throttle, raw.throttle, (raw.throttle > o.throttle ? 6 : 10) * dt);
      o.brake = approach(o.brake, raw.brake, (raw.brake > o.brake ? 7 : 12) * dt);
    } else {
      // Analog: light smoothing only (dead-zone/linearity applied by the device layer).
      const k = 1 - Math.exp(-dt / 0.02);
      o.steer += (clamp(raw.steer, -1, 1) - o.steer) * k;
      o.throttle += (clamp(raw.throttle, 0, 1) - o.throttle) * k;
      o.brake += (clamp(raw.brake, 0, 1) - o.brake) * k;
    }
    o.handbrake = clamp(raw.handbrake, 0, 1);
    o.shiftUp = raw.shiftUp;
    o.shiftDown = raw.shiftDown;
    return o;
  }

  reset(): void {
    const o = this.out;
    o.steer = 0;
    o.throttle = 0;
    o.brake = 0;
    o.handbrake = 0;
    o.shiftUp = false;
    o.shiftDown = false;
  }
}

/** Gamepad stick shaping: dead-zone then a power curve (linearity 1 = linear). */
export function shapeAxis(x: number, deadZone: number, linearity: number): number {
  const a = Math.abs(x);
  if (a <= deadZone) return 0;
  const n = (a - deadZone) / (1 - deadZone);
  return Math.sign(x) * Math.pow(clamp(n, 0, 1), linearity);
}
